import React, { Fragment, ReactNode, useRef, useState } from 'react'
import { App, Checkbox, Form, Input, Modal } from 'antd'
import { useIntl } from 'react-intl'

import Api from '@vacationtracker/shared/services/api'
import { SubscriptionPlan, SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { SubscriptionPeriod } from '@vacationtracker/shared/types/billing'
import { StripePaymentStatus } from '@vacationtracker/shared/types/stripe'

import IntlMessages from '../../util/IntlMessages'
import { track } from '../../services/analytics/analytics'
import { getAnalyticsProperties } from '../../util/analytics-helpers'
import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { COMPLETE_TRIAL_FEATURES } from './constants'
import { markCompleteTrialRequested } from './request-storage'

export interface ITryCompleteRequestModalProps {
  onCancel: () => void
  onSubmitted: () => void
  currentPlan: SubscriptionPlan
  currentPeriod: SubscriptionPeriod
  activeUsers: number
  stripePaymentStatus?: StripePaymentStatus
  upcomingInvoiceInfo?: ReactNode
}

interface IFormValues {
  features: string[]
  problem?: string
  timeSensitive?: string
}

const TryCompleteRequestModal = ({
  onCancel,
  onSubmitted,
  currentPlan,
  currentPeriod,
  activeUsers,
  stripePaymentStatus,
  upcomingInvoiceInfo,
}: ITryCompleteRequestModalProps): React.ReactElement => {
  const [form] = Form.useForm<IFormValues>()
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser } = useAppSelector(selectAuthUserSlice)

  const [isSending, setIsSending] = useState(false)
  const formStartedTracked = useRef(false)

  const isFreemium = currentPlan === SubscriptionPlanEnum.freemium

  const onFeaturesChange = (features: string[]) => {
    if (formStartedTracked.current || features.length === 0) {
      return
    }
    formStartedTracked.current = true
    track('COMPLETE_TRIAL_FORM_STARTED', getAnalyticsProperties({ activeUsers, stripePaymentStatus }))
  }

  const sendRequest = async () => {
    const values = await form.validateFields().catch(() => null)
    if (!values) {
      return
    }
    try {
      setIsSending(true)
      await Api.post('/core/event', {
        eventGroup: 'FEEDBACK',
        eventType: 'COMPLETE_TRIAL_REQUEST_CREATED',
        features: values.features,
        problem: values.problem,
        timeSensitive: values.timeSensitive,
      })

      track('COMPLETE_TRIAL_REQUESTED', {
        ...getAnalyticsProperties({ activeUsers, stripePaymentStatus }),
        featuresSelected: values.features,
        hasTimeSensitive: Boolean(values.timeSensitive?.trim()),
      })

      if (authCompany?.id) {
        markCompleteTrialRequested(authCompany.id)
      }
      onSubmitted()
    } catch (error) {
      if (error?.outOfDate) {
        return
      }
      const errorDescription = error.response?.data?.message || error.message || JSON.stringify(error)
      notification.error({
        message: formatMessage({ id: 'error.generic' }),
        description: errorDescription,
        duration: 0,
      })
    } finally {
      setIsSending(false)
    }
  }

  const summaryRows: [string, ReactNode][] = [
    ['completeTrialRequest.summary.company', authCompany?.name],
    ['completeTrialRequest.summary.companyId', authCompany?.id],
    ['completeTrialRequest.summary.admin', authUser.email],
    [
      'completeTrialRequest.summary.plan',
      <>
        {currentPlan}
        {!isFreemium && (
          <>
            {' '}
            <IntlMessages id={currentPeriod === 'annual' ? 'app.billing.periodAnnual' : 'app.monthly'} />
          </>
        )}
      </>,
    ],
    ['completeTrialRequest.summary.activeUsers', activeUsers],
    ...(!isFreemium && upcomingInvoiceInfo ? [['completeTrialRequest.summary.nextInvoice', upcomingInvoiceInfo] as [string, ReactNode]] : []),
    ['completeTrialRequest.summary.platform', authCompany?.platform],
  ]

  return (
    <Modal
      title={
        <>
          <IntlMessages id="completeTrialRequest.modal.title" />
          <div className="try-complete-request-modal-subtitle">
            <IntlMessages id="completeTrialRequest.modal.subtitle" />{' '}
            <IntlMessages id={isFreemium ? 'completeTrialRequest.reassurance.freemium' : 'completeTrialRequest.reassurance.paying'} />
          </div>
        </>
      }
      className="try-complete-request-modal"
      open
      onCancel={onCancel}
      onOk={sendRequest}
      okText={<IntlMessages id="completeTrialRequest.modal.send" />}
      cancelText={<IntlMessages id="app.cancel" />}
      confirmLoading={isSending}
      width={560}
    >
      <div className="try-complete-request-summary">
        <div className="try-complete-request-summary-title">
          <IntlMessages id="completeTrialRequest.modal.sentWithYourRequest" />
        </div>
        <dl>
          {summaryRows.map(([labelId, value]) => (
            <Fragment key={labelId}>
              <dt>
                <IntlMessages id={labelId} />
              </dt>
              <dd>{value}</dd>
            </Fragment>
          ))}
        </dl>
      </div>

      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="features"
          label={<IntlMessages id="completeTrialRequest.form.featuresLabel" />}
          rules={[{ required: true, message: formatMessage({ id: 'completeTrialRequest.form.featuresRequired' }) }]}
        >
          <Checkbox.Group className="try-complete-request-features" onChange={onFeaturesChange}>
            {COMPLETE_TRIAL_FEATURES.map((feature) => (
              <Checkbox key={feature.value} value={feature.value}>
                <IntlMessages id={feature.labelKey} />
              </Checkbox>
            ))}
          </Checkbox.Group>
        </Form.Item>

        <Form.Item name="problem" label={<IntlMessages id="completeTrialRequest.form.problemLabel" />}>
          <Input.TextArea rows={2} maxLength={1000} placeholder={formatMessage({ id: 'completeTrialRequest.form.problemPlaceholder' })} />
        </Form.Item>

        <Form.Item name="timeSensitive" label={<IntlMessages id="completeTrialRequest.form.timeSensitiveLabel" />}>
          <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} maxLength={500} placeholder={formatMessage({ id: 'completeTrialRequest.form.timeSensitiveHint' })} />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default TryCompleteRequestModal
